import { z } from "zod";
import {
  emailSchema,
  phoneSchema,
  reasonSchema,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

export const requestMagicLinkSchema = z.object({
  email: emailSchema,
  /** Where to land after signing in. Validated as a same-site path, never an absolute URL — an
   *  open redirect on a magic link hands the session to whoever crafted the link. */
  redirectTo: z
    .string()
    .max(500)
    .regex(/^\/[^/\\]/, "Must be a path on this site")
    .optional(),
});

export const consumeMagicLinkSchema = z.object({
  token: z.string().min(20).max(200),
  deviceId: z.string().min(8).max(100).optional(),
});

/**
 * What a new password has to satisfy.
 *
 * Length is the requirement, and it is the only one. Composition rules ("one capital, one symbol")
 * measurably push people towards `Password1!` and towards writing it down, which is the opposite of
 * the goal; twelve characters of anything they will actually remember is stronger than eight of
 * theatre. The upper bound is there because PBKDF2 hashes whatever it is handed and a megabyte of
 * password is a way to spend the CPU budget, not a way to be secure.
 */
export const passwordSchema = z
  .string()
  .min(12, "Use at least 12 characters — a short phrase is ideal")
  .max(200);

export const passwordSignInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  deviceId: z.string().min(8).max(100).optional(),
});

/**
 * Choose a new password.
 *
 * Either proves the right to do so: the current password (an ordinary change), or a reset token
 * (a forgotten one). Exactly one — a request carrying both is malformed rather than doubly
 * authorised, and a request carrying neither is only valid for a session that is already sitting
 * in the forced-reset state.
 */
export const setPasswordSchema = z
  .object({
    password: passwordSchema,
    currentPassword: z.string().min(1).max(200).optional(),
    token: z.string().min(20).max(200).optional(),
  })
  .refine((value) => !(value.currentPassword && value.token), {
    message: "Send either the current password or a reset token, not both",
    path: ["token"],
  });

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type PasswordSignInInput = z.infer<typeof passwordSignInSchema>;
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

export const createInvitationSchema = z.object({
  email: emailSchema,
  roleId: ulidSchema,
  expiresInHours: z.number().int().min(1).max(720).default(168),
});

/**
 * Accept an invitation and choose a password in one step.
 *
 * The password is what makes the account usable the way staff actually use it — a credential that
 * works at a gate with no signal, rather than one more link to fetch from an inbox. It is ignored
 * for an address that already has an account (a member invited into a second community keeps the
 * password they already chose); the field is still required because the form cannot ask the server
 * "does this person already exist" without turning acceptance into an account-enumeration oracle.
 */
export const acceptInvitationSchema = z.object({
  token: z.string().min(20).max(200),
  name: shortTextSchema,
  password: passwordSchema,
});

/**
 * Granting a role lives in `members.ts`, with the rest of the membership vocabulary.
 *
 * The placeholder that used to sit here restated the scope list by hand instead of using
 * `GRANT_SCOPES`, took the user in the body rather than in the path, and had neither refinement
 * the repository actually enforces. Nothing imported it. It is gone rather than kept alongside,
 * because two schemas named for the same act is how the two ends of a form start disagreeing.
 */

export const revokeGrantSchema = z.object({
  reason: reasonSchema.optional(),
});

/**
 * Self-service profile edits. No permission attaches to this — a person owns their own name —
 * which is why it is deliberately narrower than what an administrator can change (`status` and
 * `email` are not here: one is a moderation act, the other is the sign-in identity itself).
 */
export const updateProfileSchema = z.object({
  name: shortTextSchema.optional(),
  preferredName: shortTextSchema.nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  locale: z.string().min(2).max(20).nullable().optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export type RequestMagicLinkInput = z.infer<typeof requestMagicLinkSchema>;
